import {
    api_eateries_search_post,
    EateryAudience,
    EateryDecor,
    EateryDomain,
    EateryEvent,
    EateryFilter,
    EateryGroup,
    EaterySearch,
    EaterySort,
    EaterySortItem,
} from 'api'
// import { LOCALE } from 'locale'
// import { createMemo } from 'solid-js'
import { createStore, produce, unwrap } from 'solid-js/store'
import { isInCountry, location, map } from './map'
import {
    addAnd,
    cloneDefault,
    isMobile,
    isOpenNow,
    shuffleInPlace,
} from 'shared'
import { self, user_has_home } from './user'
import { eatery_state_clear } from './eatery'
import { normalizeOh } from 'shared/opening_hours'
import {
    trackSearchEvent,
    trackSearchNoResult,
    updateTrackSearchForSession,
} from 'shared/umami-events'
import {
    bboxCenter,
    bboxToLngLatBounds,
    LandmarkLocation,
    LARGE_CITY_CANTONS,
    SimpleCanton,
} from 'shared/landmark'

export let searchedAndOpenedEatery = false
export const updateSearchedAndOpenedEatery = (u: boolean) =>
    (searchedAndOpenedEatery = u)

export type SEARCH_STATE = {
    show: boolean

    searchFinished: boolean
    searchingInResults: boolean
    searchResultId: number | null

    query: string

    eateries: EaterySearch[]
    other_eateries: EaterySearch[]
    global_eateries: EaterySearch[]

    show_dropdown: boolean

    activeDecor: EateryDecor | null
    activeAudience: EateryAudience | null
    // group / domain -> GD
    activeGD:
        | {
              kind: 'group'
              value: EateryGroup
          }
        | {
              kind: 'domain'
              value: EateryDomain
          }
        | null
    // activeCat: EateryCategory | null
    // activeTheme: EateryTheme | null

    activeEvent: EateryEvent

    activeLoc:
        | {
              kind: 'sector'
              name: string
              lat: number
              lng: number
              radius: number
              regionId: string
              cantonId: string
          }
        // | {
        //       kind: 'map'
        //       lat: number
        //       lng: number
        //   }
        | {
              kind: 'user'
              loading: boolean
          }
        | {
              kind: 'home'
          }
        | null

    searchExtra: 'filters' | 'loc' | 'category' | 'theme' | null

    filters: {
        random: boolean
        open: boolean
        stars: boolean
        h_menu: boolean
        h_photos: boolean
        is_247: boolean
        never_went: boolean
        holds_bday: boolean
    }
    additionalFilters: {
        discount: boolean
    }

    sort: EaterySort | null
}

export const DEFAULT_SEARCH: SEARCH_STATE = {
    show: false,

    searchFinished: false,
    searchingInResults: false,
    searchResultId: null,

    eateries: [],
    other_eateries: [],
    global_eateries: [],

    query: '',
    show_dropdown: false,

    activeAudience: null,
    activeDecor: null,
    activeGD: null,

    activeEvent: 'none',

    activeLoc: null,

    searchExtra: null,

    filters: {
        h_menu: false,
        h_photos: false,
        is_247: false,
        open: false,
        random: false,
        stars: false,
        never_went: false,
        holds_bday: false,
    },

    additionalFilters: {
        discount: false,
    },

    sort: null,
}

const [search, setSearch] = createStore<SEARCH_STATE>(
    cloneDefault(DEFAULT_SEARCH)
)

export function search_filters_reset() {
    setSearch('filters', { ...DEFAULT_SEARCH['filters'] })
}
export function search_addional_filters_reset() {
    setSearch('additionalFilters', { ...DEFAULT_SEARCH['additionalFilters'] })
}
export function search_addional_sort_reset() {
    setSearch('sort', null)
}

export const reset_search_main_filters = (
    keep?: ('gd' | 'decor' | 'audience' | 'event')[]
) => {
    setSearch(
        produce(s => {
            if (!keep || !keep.includes('gd')) s.activeGD = null
            if (!keep || !keep.includes('decor')) s.activeDecor = null
            if (!keep || !keep.includes('audience')) s.activeAudience = null
        })
    )
}

export const search_reset = (hide: boolean = true) => {
    const newSearch = cloneDefault(DEFAULT_SEARCH)
    newSearch.show = !hide

    setSearch(newSearch)
}

export const toggleSearchExtra = (extra: SEARCH_STATE['searchExtra']) => {
    setSearch('searchExtra', extra)
}

// const GLOBAL_ZOOM_THRESHOLD = 7

export type SearchOpts = {
    forceNoFilters?: boolean
    clearEatery?: boolean
}
export let searchApiBase = {
    lat: 0,
    lng: 0,
}
export async function searchApi(args?: SearchOpts) {
    if (self.loading || !search.show) return

    const clear = args?.clearEatery ?? true

    const searchId = performance.now()

    if (clear) {
        eatery_state_clear()
    }
    setSearch({
        // searchFinished: false,
        searchingInResults: true,
        searchResultId: searchId,
        eateries: [],
        other_eateries: [],
        global_eateries: [],
    })
    searchApiBase = {
        lat: 0,
        lng: 0,
    }

    let lat = 0
    let lng = 0
    let customRadius: number | null = null

    if (search.activeLoc?.kind == 'home' && user_has_home()) {
        ;[lat, lng] = [
            self.user!.home_location.latitude,
            self.user!.home_location.longitude,
        ]
        if (!isMobile()) {
            mapGoToLocation(
                {
                    center: {
                        x: lng,
                        y: lat,
                    },
                },
                14
            )
        }
    } else if (search.activeLoc?.kind == 'user' && location.userLat !== 0) {
        ;[lat, lng] = [location.userLat, location.userLng]
        if (!isMobile()) {
            mapGoToLocation({
                center: {
                    x: lng,
                    y: lat,
                },
            })
        }
        // } else if (search.activeLoc?.kind == 'map') {
        //     ;[lat, lng] = [search.activeLoc.lat, search.activeLoc.lng]
    } else if (search.activeLoc?.kind == 'sector') {
        ;[lat, lng] = [search.activeLoc.lat, search.activeLoc.lng]

        customRadius = search.activeLoc.radius
    }

    if (!lat || !lng) {
        toggleSearchExtra('loc')
        setSearch({
            searchingInResults: true,
            activeLoc: null,
        })
        globalThis.LAST_SEARCH_LOC = null

        return
    }

    searchApiBase = {
        lat,
        lng,
    }

    if (args?.forceNoFilters) {
        setSearch({
            query: '',
            filters: {
                ...DEFAULT_SEARCH['filters'],
            },
            additionalFilters: {
                ...DEFAULT_SEARCH['additionalFilters'],
            },
        })
    }

    // reset
    searchedAndOpenedEatery = false

    // track search
    trackSearchEvent(cloneDefault(search), args)

    updateTrackSearchForSession()

    eaterySearch({
        coords: {
            lat,
            lng,
        },
        noFilter: args?.forceNoFilters,
        searchId,
        customRadius,
    })
}

export function searchInit(reset = true, clearEatery = true) {
    if (reset) {
        reset_search_main_filters()
    }

    setSearch('show', true)

    if (globalThis.LAST_SEARCH_LOC) {
        setSearch('activeLoc', globalThis.LAST_SEARCH_LOC)
    } else if (user_has_home()) {
        setSearch('activeLoc', {
            kind: 'home',
        })
    }

    searchApi({
        clearEatery,
    })
}

export type Coords = {
    lat: number
    lng: number
}

type SearchProps = {
    coords: Coords
    noFilter?: boolean
    searchId: number | null
    customRadius: number | null
}

function eatery_search_filters(
    exclude?: ('decor' | 'audience' | 'GD' | 'event')[]
): EateryFilter[] {
    let filters: EateryFilter[] = []

    filters.push({
        kind: 'flags',
        value: {
            is_close: search.filters.open ? false : null,
            is_24_7: search.filters.is_247 ? true : null,
            is_low_quality: false,
            holds_birthday_parties: search.filters.holds_bday ? true : null,
        },
    })

    addAnd(filters)

    if (search.additionalFilters.discount) {
        filters.push({
            kind: 'discount_percentage',
            value: {
                opt: 'gt',
                value: 0,
            },
        })

        addAnd(filters)

        filters.push({
            kind: 'group',
            value: [
                {
                    kind: 'discount_expires_at',
                    value: {
                        opt: 'gt',
                        value: Math.floor(Date.now() / 1e3),
                    },
                },
                {
                    kind: 'or',
                },
                {
                    kind: 'discount_expires_at',
                    value: {
                        opt: 'eq',
                        value: 0,
                    },
                },
            ],
        })

        addAnd(filters)
    }

    if (search.activeGD && !exclude?.includes('GD')) {
        const gd = search.activeGD

        if (gd.kind === 'group') {
            filters.push({
                kind: 'group',
                value: [
                    {
                        kind: 'eatery_group',
                        value: {
                            value: gd.value,
                            opt: 'eq',
                        },
                    },
                    {
                        kind: 'or',
                    },
                    {
                        kind: 'domain',
                        value: {
                            value: gd.value == 'unknown' ? 'none' : gd.value,
                            opt: 'eq',
                        },
                    },
                ],
            })
        } else {
            filters.push({
                kind: 'domain',
                value: {
                    value: gd.value,
                    opt: 'eq',
                },
            })
        }

        addAnd(filters)
    }

    if (search.activeAudience && !exclude?.includes('audience')) {
        filters.push({
            kind: 'audience',
            value: {
                value: search.activeAudience,
                opt: 'eq',
            },
        })

        addAnd(filters)
    }

    if (search.activeDecor && !exclude?.includes('decor')) {
        filters.push({
            kind: 'decor',
            value: {
                value: search.activeDecor,
                opt: 'eq',
            },
        })

        addAnd(filters)
    }

    if (search.activeEvent !== 'none' && !exclude?.includes('event')) {
        filters.push({
            kind: 'event',
            value: {
                value: search.activeEvent,
                opt: 'eq',
            },
        })

        addAnd(filters)
    }

    if (search.filters.never_went) {
        filters.push({
            kind: 'recently_naved',
            value: false,
        })

        addAnd(filters)
    }

    if (search.filters.stars) {
        filters.push({
            kind: 'stars',
            value: {
                opt: 'eq',
                value: 5,
            },
        })

        addAnd(filters)
    }

    if (search.filters.h_photos) {
        filters.push({
            kind: 'has_photo',
            value: true,
        })

        addAnd(filters)
    }

    if (search.filters.h_menu) {
        filters.push({
            kind: 'dish_count',
            value: {
                opt: 'gt',
                value: 0,
            },
        })

        addAnd(filters)
    }

    filters.pop()

    return filters
}
function eatery_search_sort(coords: SearchProps['coords']): EaterySortItem[] {
    const sorts: EaterySortItem[] = []
    const userSort = unwrap(search.sort)

    const addSort = (item: EaterySortItem) => {
        const kind = item.by.kind
        const exists = sorts.some(s => s.by.kind === kind)
        if (!exists) sorts.push(item)
    }

    if (userSort) {
        if (userSort === ('geo_point' as any)) {
            addSort({
                by: { kind: 'geo_point', lat: coords.lat, lng: coords.lng },
                order: 'asc',
            })
        } else {
            addSort({
                by: { kind: userSort as any },
                order: 'desc',
            })
        }
    } else {
        if (search.activeEvent != 'none') {
            addSort({
                by: { kind: 'geo_point', lat: coords.lat, lng: coords.lng },
                order: 'asc',
            })
        }
    }

    addSort({ by: { kind: 'is_tender' }, order: 'desc' })
    addSort({ by: { kind: 'is_unique' }, order: 'desc' })
    // addSort({ by: { kind: 'is_famous' }, order: 'desc' })
    addSort({ by: { kind: 'is_brand' }, order: 'desc' })
    addSort({ by: { kind: 'quality_score' }, order: 'desc' })

    return sorts
}

async function eaterySearch(args: SearchProps) {
    const { lat, lng } = args.coords

    const name = unwrap(search.query) || ''

    const serverSearch = async (radius: number): Promise<EaterySearch[]> => {
        const km = radius * 1e3

        const filters: EateryFilter[] = eatery_search_filters()
        const sort: EaterySortItem[] = eatery_search_sort(args.coords)

        if (lat && lng) {
            if (filters.length > 0) addAnd(filters)

            filters.push({
                kind: 'geo_radius',
                value: {
                    distance_in_meters: km,
                    lat,
                    lng,
                },
            })
        }

        const res = await api_eateries_search_post(
            {
                name,
                filters,
                sort,
                page: 0,
            },
            {
                show_alert: false,
            }
        )

        let result: EaterySearch[] = []

        if (res.ok()) result = res.body

        if (search.filters.open) {
            result = result.filter(
                eat =>
                    isOpenNow(normalizeOh(eat.opening_hours)) ||
                    eat.flags.is_24_7
            )
        }

        if (search.filters.random) {
            shuffleInPlace(result)
        }

        return result
    }

    const othersSearch = async (
        globalSeaching = false
    ): Promise<EaterySearch[]> => {
        const km = choosenKm * 1e3

        const filters: EateryFilter[] = eatery_search_filters([
            'decor',
            'audience',
        ])
        const sort: EaterySortItem[] = eatery_search_sort(args.coords)

        if (!globalSeaching) {
            if (lat && lng) {
                if (filters.length > 0) addAnd(filters)

                filters.push({
                    kind: 'geo_radius',
                    value: {
                        distance_in_meters: km,
                        lat,
                        lng,
                    },
                })
            }
        }

        const res = await api_eateries_search_post(
            {
                name: globalSeaching ? name : '',
                filters,
                sort,
                page: 0,
            },
            {
                show_alert: false,
            }
        )

        let result: EaterySearch[] = []

        if (res.ok()) result = res.body

        if (search.filters.open) {
            result = result.filter(
                eat =>
                    isOpenNow(normalizeOh(eat.opening_hours)) ||
                    eat.flags.is_24_7
            )
        } else {
            const nowOpen = result.filter(
                eat =>
                    isOpenNow(normalizeOh(eat.opening_hours)) ||
                    eat.flags.is_24_7
            )
            const nowClosed = result.filter(
                eat =>
                    !isOpenNow(normalizeOh(eat.opening_hours)) &&
                    !eat.flags.is_24_7
            )

            result = [...nowOpen, ...nowClosed]
        }

        return result
    }

    let choosenKm: number = args.customRadius || 50 // city wide

    const currentSearchId = args.searchId

    let eateries = await serverSearch(choosenKm)

    if (currentSearchId) {
        // return if search session expired!
        if (currentSearchId !== search.searchResultId) return
    }

    if (search.additionalFilters.discount) {
        eateries = eateries.filter(e => e.discount_available)
    }

    setSearch(
        produce(s => {
            s.eateries = eateries

            // if no eateries found, search is not finished
            s.searchFinished = eateries.length != 0
            s.searchingInResults = false
        })
    )

    // UPDATE:
    // DO THE OTHER SEARCH EVEN IF WE HAVE LESS THAT 10 EATERIES
    // TO SHOW ON THAT THEME.
    if (eateries.length == 0 && name) {
        let globalRes = await othersSearch(true)

        if (currentSearchId !== search.searchResultId) return

        setSearch(
            produce(s => {
                s.global_eateries = globalRes
                s.searchFinished = true
                s.searchingInResults = false
            })
        )
    } else if (eateries.length < 10) {
        // can search for others
        if (search.activeDecor || search.activeAudience) {
            let othersRes = await othersSearch()

            if (currentSearchId !== search.searchResultId) return

            const eateryGenes = new Set(eateries.map(e => e.gene))

            othersRes = othersRes.filter(
                eatery => !eateryGenes.has(eatery.gene)
            )

            setSearch(
                produce(s => {
                    s.other_eateries = othersRes
                    s.searchFinished = true
                    s.searchingInResults = false
                })
            )
        } else {
            setSearch({ searchFinished: true })

            trackSearchNoResult(search, { lat, lng })
        }
    }
}

export let searchPageRef: HTMLElement
export const setSearchPageRef = (e: HTMLElement) => (searchPageRef = e)

export { search, setSearch }

export const saveBeforeSearchMapData = () => {
    if (!map || isMobile()) return

    const center = map.getCenter()

    if (!isInCountry(center.lat, center.lng)) {
        globalThis.MAP_INFO_BEFORE_SEARCHING = null
        return
    }

    globalThis.MAP_INFO_BEFORE_SEARCHING = {
        zoom: Math.min(map.getZoom(), 15),
        lat: center.lat,
        lng: center.lng,
    }
}

export const mapGoToSector = (sector: SimpleCanton) => {
    if (!map) return

    const center =
        sector.center ??
        (() => {
            const [lng, lat] = bboxCenter(sector.bounding_box)

            return {
                x: lng,
                y: lat,
            }
        })()

    const maxZoom = LARGE_CITY_CANTONS.has(sector.id) ? 10 : 13

    map.flyTo({
        center: [center.x, center.y],
        zoom: maxZoom,
        duration: 600,
    })
}

export const mapGoToLocation = (loc: LandmarkLocation, zoom = 15) => {
    if (!map) return

    if (loc.center) {
        map.flyTo({
            center: [loc.center.x, loc.center.y],
            zoom,
            duration: 600,
            animate: true,
        })

        return
    }

    if (loc.bbox) {
        map.fitBounds(bboxToLngLatBounds(loc.bbox), {
            padding: 80,
            maxZoom: 15,
            duration: 600,
        })
    }
}

export const mapGoToBeforeSearch = () => {
    if (!map || !MAP_INFO_BEFORE_SEARCHING) {
        return
    }

    const info = MAP_INFO_BEFORE_SEARCHING

    map.flyTo({
        center: [info.lng, info.lat],
        zoom: info.zoom,
        duration: 600,
        essential: true,
    })

    globalThis.MAP_INFO_BEFORE_SEARCHING = null
}
